import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, Observable, of, switchMap, take } from 'rxjs';

import { UserGetFailure, UserGetRequest, UserGetSuccess } from '../../store/user/user.actions';
import { User } from '../../store/user/user.models';
import { selectUser } from '../../store/user/user.selectors';

function loadCurrentUser(): Observable<User | null> {
  const store = inject(Store);
  const actions$ = inject(Actions);

  return store.select(selectUser).pipe(
    take(1),
    switchMap((user) => {
      if (user) {
        return of(user);
      }

      store.dispatch(UserGetRequest());
      return actions$.pipe(
        ofType(UserGetSuccess, UserGetFailure),
        take(1),
        map((action) => ('user' in action ? action.user : null)),
      );
    }),
  );
}

function normalizeRole(user: User | null): string {
  const value = typeof user?.role === 'string' ? user.role.trim().toLowerCase() : '';
  if (value === 'super_admin') {
    return 'superadmin';
  }
  return value;
}

export const superAdminGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);

  return loadCurrentUser().pipe(
    map((user) => (normalizeRole(user) === 'superadmin' ? true : router.createUrlTree(['/']))),
  );
};

export const nonSuperAdminGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const router = inject(Router);

  return loadCurrentUser().pipe(
    map((user) => (normalizeRole(user) === 'superadmin' ? router.createUrlTree(['/platform/dashboard']) : true)),
  );
};
